import * as Dfns from 'date-fns/fp';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd, MdContentCopy, MdDelete, MdPlayArrow } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Button } from '@Components/Button';
import { Appearance, Background, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  ConstraintListItem,
  CopyConstraintParams,
  DeleteConstraintParams,
  NewConstraintParams,
  Optimization,
  OptimizationsContext,
  ROUTES,
  RunOptimizationParams
} from './optimizations-constants';
import './OptimizationHome.scss';

const DATE_FORMAT = 'yyyy-MM-dd hh:mma';

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  return Dfns.format(DATE_FORMAT)(new Date(dateString));
};

const getConstraintNameError = (name: string): string | null => {
  if (name.length === 0) return 'Name is required';
  if (name === '.' || name === '..') return 'Invalid name';
  const foundChars = name.match(/[\\/=@?#&%$:]/g);
  if (foundChars) {
    return `Invalid characters: ${Array.from(new Set(foundChars)).join(', ')}`;
  }
  return null;
};

const isConstraintNameValid = (name: string): boolean => !getConstraintNameError(name);

interface NewConstraintModalProps {
  onHide: () => void;
  onSubmit: (params: NewConstraintParams) => void;
}

const NewConstraintModal = ({ onHide, onSubmit }: NewConstraintModalProps) => {
  const [name, setName] = useState('');
  const nameError = name.length > 0 ? getConstraintNameError(name) : null;

  return (
    <Modal size='lg' keyboard={false} show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>New Constraint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control
            autoFocus
            type='text'
            value={name}
            isInvalid={Boolean(nameError)}
            onChange={event => setName(event.target.value.replace(/ /g, ''))}
          />
          <Form.Control.Feedback type='invalid'>{nameError}</Form.Control.Feedback>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          design={Intent.SUCCESS}
          disabled={!isConstraintNameValid(name)}
          onClick={() => onSubmit({ name })}
        >
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface CopyConstraintModalProps {
  fromConstraintId: string;
  onHide: () => void;
  onSubmit: (params: CopyConstraintParams) => void;
}

const CopyConstraintModal = ({ fromConstraintId, onHide, onSubmit }: CopyConstraintModalProps) => {
  const [name, setName] = useState('');
  const nameError = name.length > 0 ? getConstraintNameError(name) : null;

  return (
    <Modal size='lg' keyboard={false} show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Copy Constraint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>From</Form.Label>
          <Form.Control type='text' value={fromConstraintId} readOnly />
        </Form.Group>
        <Form.Group>
          <Form.Label>To</Form.Label>
          <Form.Control
            autoFocus
            type='text'
            value={name}
            isInvalid={Boolean(nameError)}
            onChange={event => setName(event.target.value.replace(/ /g, ''))}
          />
          <Form.Control.Feedback type='invalid'>{nameError}</Form.Control.Feedback>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          design={Intent.SUCCESS}
          disabled={!isConstraintNameValid(name)}
          onClick={() => onSubmit({ from: fromConstraintId, to: name })}
        >
          Copy
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface DeleteConstraintModalProps {
  deleteConstraintId: string;
  onHide: () => void;
  onSubmit: (params: DeleteConstraintParams) => void;
}

const DeleteConstraintModal = ({
  deleteConstraintId,
  onHide,
  onSubmit
}: DeleteConstraintModalProps) => (
  <Modal size='lg' keyboard={false} show onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Delete Constraint {deleteConstraintId}?</Modal.Title>
    </Modal.Header>
    <Modal.Footer>
      <Button design={Intent.ERROR} onClick={() => onSubmit({ deleteConstraintId })}>
        Delete
      </Button>
    </Modal.Footer>
  </Modal>
);

interface RunOptimizationModalProps {
  runOptimizationId: string;
  onHide: () => void;
  onSubmit: (params: RunOptimizationParams) => void;
}

const RunOptimizationModal = ({
  runOptimizationId,
  onHide,
  onSubmit
}: RunOptimizationModalProps) => (
  <Modal size='lg' keyboard={false} show onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Run Constraint</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group>
        <Form.Label>Name</Form.Label>
        <Form.Control type='text' value={runOptimizationId} readOnly />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button design={Intent.SUCCESS} onClick={() => onSubmit({ name: runOptimizationId })}>
        <MdPlayArrow /> Run
      </Button>
    </Modal.Footer>
  </Modal>
);

export const OptimizationHome = () => {
  const navigate = useNavigate();
  const { optimizations, constraintsList, copyConstraint, deleteConstraint, runOptimization } =
    useContext(OptimizationsContext);

  const [constraintModal, setConstraintModal] = useState<
    { mode: 'new' } | { mode: 'copy'; fromConstraintId: string }
  >();
  const [deleteTarget, setDeleteTarget] = useState<string>();
  const [runTarget, setRunTarget] = useState<string>();
  const [optimizationFilterBar, setOptimizationFilterBar] = useState<HTMLDivElement | null>(null);
  const [constraintFilterBar, setConstraintFilterBar] = useState<HTMLDivElement | null>(null);

  const optimizationHeaders: TypedHeader<Optimization>[] = useMemo(
    () => [
      {
        label: 'Number',
        name: 'build_number',
        width: 120,
        renderer: row =>
          row.status?.includes('SUCC') ? (
            <Button
              appearance={Appearance.EMPTY}
              size={Size.SM}
              tooltip={{ text: 'View optimization results' }}
              onClick={() => navigate(`${ROUTES.RUNS.uri}/${row.build_number}`)}
            >
              {row.build_number}
            </Button>
          ) : (
            row.build_number
          )
      },
      {
        label: 'Constraint',
        name: 'name',
        flex: 2,
        minFlexWidth: 200,
        renderer: row => row.constraint_name || 'N/A'
      },
      { label: 'Status', name: 'status', width: 180, renderer: row => row.status || 'Unknown' },
      {
        label: 'Date (Local Time)',
        name: 'date',
        width: 200,
        renderer: row => formatDate(row.date)
      }
    ],
    [navigate]
  );

  const constraintHeaders: TypedHeader<ConstraintListItem>[] = useMemo(
    () => [
      {
        label: 'Name',
        name: 'name',
        width: 160,
        renderer: row => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => navigate(`${ROUTES.CONSTRAINTS.uri}/${row.name}`)}
          >
            {row.name}
          </Button>
        )
      },
      {
        label: 'Last Modified',
        name: 'lastmodified',
        flex: 1,
        // Keep the flex solver from starving the date column below the timestamp's
        // intrinsic width (which made dates wrap onto two lines and overlap rows).
        minFlexWidth: 150,
        renderer: row => formatDate(row.lastmodified)
      },
      {
        label: '',
        name: 'actions',
        width: 120,
        nonInteractive: true,
        nonFilterable: true,
        renderer: row => (
          <div className='constraint-actions'>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              aria-label={`Run ${row.name}`}
              tooltip={{ text: 'Run optimization with this constraint' }}
              onClick={() => setRunTarget(row.name)}
            >
              <MdPlayArrow />
            </IconButton>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              aria-label={`Copy ${row.name}`}
              tooltip={{ text: 'Copy constraint' }}
              onClick={() => setConstraintModal({ mode: 'copy', fromConstraintId: row.name })}
            >
              <MdContentCopy />
            </IconButton>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Intent.ERROR}
              size={Size.SM}
              aria-label={`Delete ${row.name}`}
              tooltip={{ text: 'Delete constraint' }}
              onClick={() => setDeleteTarget(row.name)}
            >
              <MdDelete />
            </IconButton>
          </div>
        )
      }
    ],
    [navigate]
  );

  const renderOptimizationsLoading = useCallback(
    () =>
      !optimizations ? (
        <Skeleton>
          <TableSkeleton rowHeight={25} headerHeight={25} yOffset={8} />
        </Skeleton>
      ) : (
        <TableEmptyState />
      ),
    [optimizations]
  );

  const renderConstraintsLoading = useCallback(
    () =>
      !constraintsList ? (
        <Skeleton>
          <TableSkeleton rowHeight={25} headerHeight={25} yOffset={8} />
        </Skeleton>
      ) : (
        <TableEmptyState />
      ),
    [constraintsList]
  );

  return (
    <div className='hroas-optimizations-home'>
      <WidgetContainer
        background={Background.DARK}
        className='optimization-runs-widget'
        header={
          <>
            <h2 className='table-name'>Optimizations</h2>
            <div className='widget-filter-bar-container' ref={setOptimizationFilterBar} />
          </>
        }
      >
        <BPMTable
          autoHeight
          className='optimization-table'
          headerHeight={25}
          rowHeight={25}
          data={optimizations ?? []}
          defaultTokens={{ advanced: ['Status', 'is not like', 'FAILURE'] }}
          defaultAdvancedFilter
          externalFilterBarContainer={optimizationFilterBar}
          headers={optimizationHeaders}
          noRowsRenderer={renderOptimizationsLoading}
        />
      </WidgetContainer>
      <WidgetContainer
        background={Background.DARK}
        className='constraints-widget'
        header={
          <>
            <h2 className='table-name'>Constraints</h2>
            <div className='widget-filter-bar-container' ref={setConstraintFilterBar} />
          </>
        }
        rightActions={
          <IconButton
            appearance={Appearance.OUTLINED}
            size={Size.LG}
            aria-label='New constraint'
            tooltip={{ text: 'New constraint' }}
            onClick={() => setConstraintModal({ mode: 'new' })}
          >
            <MdAdd />
          </IconButton>
        }
      >
        <BPMTable
          autoHeight
          className='constraint-list-table'
          headerHeight={25}
          rowHeight={25}
          data={constraintsList ?? []}
          externalFilterBarContainer={constraintFilterBar}
          headers={constraintHeaders}
          noRowsRenderer={renderConstraintsLoading}
        />
      </WidgetContainer>

      {constraintModal?.mode === 'new' && (
        <NewConstraintModal
          onHide={() => setConstraintModal(undefined)}
          onSubmit={({ name }) => {
            // Navigates straight to the constraint editor without creating anything yet —
            // ConstraintView reads `?new=true` to seed a blank, unsaved constraint, and the
            // real record is only created on that page's first Save.
            setConstraintModal(undefined);
            navigate(`${ROUTES.CONSTRAINTS.uri}/${name}?new=true`);
          }}
        />
      )}
      {constraintModal?.mode === 'copy' && (
        <CopyConstraintModal
          fromConstraintId={constraintModal.fromConstraintId}
          onHide={() => setConstraintModal(undefined)}
          onSubmit={async ({ from, to }) => {
            setConstraintModal(undefined);
            await copyConstraint({ from, to });
            navigate(`${ROUTES.CONSTRAINTS.uri}/${to}`);
          }}
        />
      )}
      {deleteTarget && (
        <DeleteConstraintModal
          deleteConstraintId={deleteTarget}
          onHide={() => setDeleteTarget(undefined)}
          onSubmit={params => {
            setDeleteTarget(undefined);
            deleteConstraint(params);
          }}
        />
      )}
      {runTarget && (
        <RunOptimizationModal
          runOptimizationId={runTarget}
          onHide={() => setRunTarget(undefined)}
          onSubmit={params => {
            setRunTarget(undefined);
            runOptimization(params);
          }}
        />
      )}
    </div>
  );
};
